import { useCallback, useEffect, useId, useRef, useState, type FocusEvent, type ReactNode, type RefObject,
  type SyntheticEvent } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ChevronDown } from 'lucide-react';
import type { AssistantPendingDTO } from '@contracts/assistant';
import type {
  NudgeActionCardDTO, NudgeDTO, NudgeSnoozeUntil, ProactiveRule, RunCardDTO,
} from '@contracts/assistant-proactive';
import { PROACTIVE_RULES } from '@contracts/assistant-proactive';
import type { Id } from '@contracts/common';
import { DEFAULT_PERSONA_NAME } from '@contracts/persona';
import { ApiClientError } from '../../../api/client';
import { notify } from '../../../lib/toast';
import { cn } from '../../../lib/utils';
import { useAssistantOptional } from '../AssistantProvider';
import { MISSIONS_KEY } from '../missions/api';
import { refreshFor } from '../refresh';
import {
  NUDGES_KEY, PROACTIVE_PREFS_KEY, actRunCard, disableRule, dismissNudge, dismissRun, doneNudge, editRunCard,
  snoozeNudge, undoRun, useNudges, watchRunResume,
} from './api';
import { NudgeCards, NudgeRow } from './NudgeRow';
import { RunCard, type RunCardHandlers } from './RunCard';
import { useNudgeActions } from './useNudgeActions';

/** D72: the rows shown before "Xem thêm (n)". */
export const TRAY_ROWS = 3;
const FAILED = 'Đã có lỗi xảy ra, vui lòng thử lại';
const collapsedKey = (userId: Id) => `hana.tray.collapsed.${userId}`;

function readCollapsed(userId: Id): boolean {
  try {
    return localStorage.getItem(collapsedKey(userId)) === '1';
  } catch {
    return false;                                            // nothing to read: open by default (§7.4)
  }
}

function writeCollapsed(userId: Id, collapsed: boolean): void {
  try {
    localStorage.setItem(collapsedKey(userId), collapsed ? '1' : '0');
  } catch {
    // Kept for this panel only.
  }
}

const UNFOLD_EVENT = 'hana:tray-unfold';
/**
 * S7 §9.4: the run notice opens the tray unfolded. The fold is kept for the tray about to mount (it reads it on mount),
 * and a tray already on screen hears the event and unfolds (D93).
 */
export const unfoldTray = (userId: Id): void => {
  writeCollapsed(userId, false);
  window.dispatchEvent(new CustomEvent<Id>(UNFOLD_EVENT, { detail: userId }));
};

/** The bell's run notices follow the run: a write on a run card reads them again. */
const NOTIFICATIONS_KEY = ['notifications'] as const;

/**
 * A nudge write the server answered "not any more" (D57, D89): 404 the nudge is gone, 410 its work is done, 409 it
 * moved on in another tab. Information in the server's words, never an error; anything else is one.
 */
export function tellRefusal(e: unknown): void {
  const err = e instanceof ApiClientError ? e : null;
  if (err !== null && (err.status === 404 || err.status === 409 || err.status === 410)) notify.info(err.message);
  else notify.error(err?.message || FAILED);
}

/** S9 D91: the delegated rules' rows lead the tray under "Việc đã giao"; the rest keep the server's order. */
const isDelegated = (n: NudgeDTO): boolean => n.rule.startsWith('delegated.');
export const delegatedFirst = (items: readonly NudgeDTO[]): NudgeDTO[] =>
  [...items.filter(isDelegated), ...items.filter((n) => !isDelegated(n))];

const isRule = (rule: string): rule is ProactiveRule => (PROACTIVE_RULES as readonly string[]).includes(rule);

/** The disable offer after a dismiss (§7.3), in the dismissed row's place: before the row that was under it. */
type Offer = { nudgeId: Id; rule: ProactiveRule; before: Id | null };

function DisableLine({ offer, onDone }: { offer: Offer; onDone: () => void }) {
  const queryClient = useQueryClient();
  const [saving, setSaving] = useState(false);
  const disable = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const saved = await disableRule(offer.rule);
      queryClient.setQueryData(PROACTIVE_PREFS_KEY, saved);
      notify.success('Đã tắt nhắc này');
      onDone();
      void queryClient.invalidateQueries({ queryKey: NUDGES_KEY });
    } catch (e) {
      notify.error(e instanceof ApiClientError ? e.message || FAILED : FAILED);
      setSaving(false);
    }
  };
  return (
    <li data-disable-offer={offer.nudgeId} className="mt-1.5 px-1 text-xs text-[hsl(var(--h-muted-foreground))] first:mt-0">
      Không muốn em nhắc kiểu này nữa?{' '}
      <button type="button" disabled={saving} onClick={() => { void disable(); }}
        className="min-h-11 font-semibold text-[hsl(var(--h-primary))] hover:underline disabled:opacity-60 lg:min-h-0">
        Tắt nhắc này
      </button>
    </li>
  );
}

const ENTRY = '[data-tray-entry]';
const MENU_OR_DIALOG = '[role="menu"],[role="dialog"],[role="alertdialog"]';
/** A menu or dialog an entry opened (the panel's own sheet, which holds the tray, is not one). */
const openedBy = (to: Element, tray: HTMLElement | null): boolean => {
  const host = to.closest(MENU_OR_DIALOG);
  return host !== null && (tray === null || !host.contains(tray));
};
const entryKeys = (root: HTMLElement): string[] =>
  Array.from(root.querySelectorAll<HTMLElement>(ENTRY), (el) => el.dataset.trayEntry ?? '');

/**
 * D104: the entry (run card or nudge row) that held the focus, and the entries in order at that time. When it leaves the
 * tray (Ẩn, Hoàn tác, Bỏ qua, Xong…) the focus falls to the page body: it goes to the next entry's menu, else the one
 * before, else the tray's heading; when the tray itself goes, `onEmptied` (D108). A focus that left the tray for
 * somewhere else is not followed.
 */
function useTrayFocus(section: RefObject<HTMLElement | null>, heading: RefObject<HTMLElement | null>,
  onEmptied: (() => void) | undefined) {
  const last = useRef<{ key: string; order: string[] } | null>(null);
  useEffect(() => {
    const root = section.current;
    const was = last.current;
    if (was === null) return;
    if (root === null) {
      // D108: the last entry left and the tray with it: the panel's message box takes the focus.
      last.current = null;
      const active = document.activeElement;
      if (active === null || active === document.body || !document.contains(active)) onEmptied?.();
      return;
    }
    const now = entryKeys(root);
    if (now.includes(was.key)) {
      last.current = { key: was.key, order: now };
      return;
    }
    const active = document.activeElement;
    if (active !== null && active !== document.body && document.contains(active)) return;
    const at = was.order.indexOf(was.key);
    const next = was.order.slice(at + 1).find((k) => now.includes(k))
      ?? was.order.slice(0, Math.max(at, 0)).reverse().find((k) => now.includes(k));
    last.current = null;
    const entry = next === undefined ? null
      : Array.from(root.querySelectorAll<HTMLElement>(ENTRY)).find((el) => el.dataset.trayEntry === next) ?? null;
    (entry?.querySelector<HTMLElement>('[data-tray-focus]') ?? heading.current)?.focus();
  });
  // D110: a tap that gave no focus (Safari) leaves a mark no blur in the tray clears; the focus landing anywhere else
  // but the tray or a menu/dialog it opened drops it, so a card leaving later never pulls the focus back.
  useEffect(() => {
    const away = (e: globalThis.FocusEvent) => {
      const to = e.target;
      if (!(to instanceof Element) || section.current?.contains(to)) return;
      if (!openedBy(to, section.current)) last.current = null;
    };
    document.addEventListener('focusin', away);
    return () => document.removeEventListener('focusin', away);
  }, [section]);
  // A Radix menu trigger takes no focus on a pointer press, so the press marks the entry too. Events from a portal (the
  // entry's own menu) bubble here through React but are not in the tray: they leave the mark as it is.
  const mark = (e: SyntheticEvent<HTMLElement>) => {
    const root = section.current;
    const target = e.target as HTMLElement;
    if (root === null || !root.contains(target)) return;
    const entry = target.closest<HTMLElement>(ENTRY);
    last.current = entry === null ? null : { key: entry.dataset.trayEntry ?? '', order: entryKeys(root) };
  };
  const onBlur = (e: FocusEvent<HTMLElement>) => {
    const root = section.current;
    const to = e.relatedTarget as HTMLElement | null;
    if (root === null || !root.contains(e.target) || to === null || root.contains(to)) return;
    // A menu or dialog the entry opened hands the focus back to it; anywhere else, the reader moved on.
    if (!openedBy(to, root)) last.current = null;
  };
  return { onFocus: mark, onPointerDown: mark, onBlur };
}

/**
 * "Hana muốn nhắc" (spec §7.4, D72, mockup §2 `.tray`): at the top of the panel, above the messages. The header counts
 * the shown nudges and run cards (D95; the badge counts the nudges only), and folds the tray; the fold is kept per person in localStorage.
 * Three rows, then "Xem thêm (n)". No tray while there is nothing to show. Opening the panel reads the tray again (D76).
 * Each row's action cards sit under it; the cards whose nudge has left the tray follow the rows (§7.3).
 * S7 §9.3: the reader's background runs (`runCards`, `RunCard`) sit above the nudges, also with the nudges off.
 */
export function NudgeTray({ userId, onAsk, onEmptied }: {
  userId: Id; onAsk: (text: string, nudgeId: Id) => void; onEmptied?: () => void;
}) {
  const queryClient = useQueryClient();
  const persona = useAssistantOptional()?.prefs.personaName ?? DEFAULT_PERSONA_NAME;
  const { data, refetch } = useNudges(true);
  const [collapsed, setCollapsed] = useState(() => readCollapsed(userId));
  const [expanded, setExpanded] = useState(false);
  const [offers, setOffers] = useState<Offer[]>([]);
  const [busy, setBusy] = useState<ReadonlySet<Id>>(() => new Set());
  const listId = useId();
  const headId = useId();
  const delegatedHeadId = useId();
  const sectionRef = useRef<HTMLElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const focus = useTrayFocus(sectionRef, headingRef, onEmptied);

  useEffect(() => { void refetch(); }, [refetch]);
  useEffect(() => {
    const unfold = (e: Event) => { if ((e as CustomEvent<Id>).detail === userId) setCollapsed(false); };
    window.addEventListener(UNFOLD_EVENT, unfold);
    return () => window.removeEventListener(UNFOLD_EVENT, unfold);
  }, [userId]);
  const actions = useNudgeActions({ onAsk, onRefused: tellRefusal });

  const write = useCallback(async (id: Id, call: () => Promise<unknown>): Promise<unknown> => {
    setBusy((s) => new Set(s).add(id));
    try {
      return await call();
    } catch (e) {
      tellRefusal(e);
      return null;
    } finally {
      setBusy((s) => {
        const next = new Set(s);
        next.delete(id);
        return next;
      });
      void queryClient.invalidateQueries({ queryKey: NUDGES_KEY });
    }
  }, [queryClient]);

  if (data === undefined) return null;
  // S7 §9.3: the reader's background runs show whatever the nudges' state, OFF included; the nudges only when on.
  const runCards = data.runCards ?? [];
  const items = data.enabled ? delegatedFirst(data.items) : [];
  const actionCards = data.enabled ? data.actionCards : [];
  if (items.length === 0 && actionCards.length === 0 && offers.length === 0 && runCards.length === 0) return null;

  const shown = expanded ? items : items.slice(0, TRAY_ROWS);
  const toggle = () => {
    setCollapsed((c) => {
      writeCollapsed(userId, !c);
      return !c;
    });
  };
  const snooze = (n: NudgeDTO, until: NudgeSnoozeUntil) =>
    void write(n.id, () => snoozeNudge(n.id, until, crypto.randomUUID()));
  const dismiss = (n: NudgeDTO) => {
    const at = items.findIndex((i) => i.id === n.id);
    const before = items[at + 1]?.id ?? null;
    void write(n.id, async () => {
      const answer = await dismissNudge(n.id, crypto.randomUUID());
      if (answer.offerDisableRule && isRule(n.rule)) {
        const rule = n.rule;
        setOffers((o) => [...o.filter((x) => x.rule !== rule), { nudgeId: n.id, rule, before }]);
      }
      return answer;
    });
  };
  const done = (n: NudgeDTO) => void write(n.id, () => doneNudge(n.id, crypto.randomUUID()));
  const dropOffer = (nudgeId: Id) => setOffers((o) => o.filter((x) => x.nudgeId !== nudgeId));

  const visibleIds = new Set(shown.map((n) => n.id));
  const cardsOf = (nudgeId: Id) => actionCards.filter((c) => c.nudgeId === nudgeId);
  const itemIds = new Set(items.map((n) => n.id));
  const orphans = actionCards.filter((c) => !itemIds.has(c.nudgeId));
  const forceConfirm = (c: NudgeActionCardDTO) => c.pending !== undefined && !actions.edited.has(c.pending.actionId)
    && (actions.forced.has(c.pending.actionId)
      || items.find((n) => n.id === c.nudgeId)?.actions.find((a) => a.index === c.index)?.forceConfirm === true);
  const cards = (list: readonly NudgeActionCardDTO[]) => list.length === 0 ? null : (
    <NudgeCards cards={list} forceConfirm={forceConfirm} acting={actions.acting} onAct={actions.onAct}
      onEdited={actions.onEdited} />
  );
  const offerLine = (o: Offer) => <DisableLine key={`offer-${o.nudgeId}`} offer={o} onDone={() => dropOffer(o.nudgeId)} />;
  const rowOf = (n: NudgeDTO) => (
    <NudgeRow nudge={n} busy={busy.has(n.id) || actions.running.has(n.id)} onAction={(a) => actions.onAction(n, a)}
      onDone={() => done(n)} onSnooze={(until) => snooze(n, until)} onDismiss={() => dismiss(n)}>
      {cards(cardsOf(n.id))}
    </NudgeRow>
  );

  const itemOf = (n: NudgeDTO) => (
    <ItemWithOffers key={n.id} offers={offers.filter((o) => o.before === n.id)} offerLine={offerLine}>
      {rowOf(n)}
    </ItemWithOffers>
  );
  const shownDelegated = shown.filter(isDelegated);
  const shownRest = shown.filter((n) => !isDelegated(n));

  const runWrite = (id: Id, call: () => Promise<unknown>) => write(id, async () => {
    const answer = await call();
    void queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_KEY });
    // The missions page behind the tray (lists, the orders' "Lần gần nhất", the run log) follows the run (G10).
    void queryClient.invalidateQueries({ queryKey: MISSIONS_KEY });
    return answer;
  });
  const runHandlers: RunCardHandlers = {
    busy: (id) => busy.has(id),
    onAct: (runId, actionId, kind) => {
      if (busy.has(actionId)) return;
      void runWrite(actionId, async () => {
        const done = await actRunCard(runId, actionId, kind, crypto.randomUUID());
        if (kind === 'confirm') {
          watchRunResume();
          void refreshFor(queryClient, done.objects ?? []);
        }
        return done;
      });
    },
    edit: (runId: Id, pending: AssistantPendingDTO) => async (input, requestId) => {
      await editRunCard(runId, pending.actionId, { input }, requestId);
      void queryClient.invalidateQueries({ queryKey: NUDGES_KEY });
    },
    onUndo: (card: RunCardDTO) => {
      if (busy.has(card.runId)) return;
      void runWrite(card.runId, async () => {
        const answer = await undoRun(card.runId, crypto.randomUUID());
        void refreshFor(queryClient, card.objects);
        return answer;
      });
    },
    onDismiss: (card: RunCardDTO) => { void runWrite(card.runId, () => dismissRun(card.runId, crypto.randomUUID())); },
  };
  const now = new Date();
  // D95: the header counts what the tray shows, the run cards with the nudges; the badge stays the nudges (§7.4).
  const count = data.badgeCount + runCards.length;

  return (
    <section ref={sectionRef} aria-labelledby={headId} data-nudge-tray="" onFocusCapture={focus.onFocus}
      onPointerDownCapture={focus.onPointerDown} onBlurCapture={focus.onBlur}
      className="mx-3 mt-2 flex min-h-0 shrink flex-col overflow-y-auto rounded-[10px] bg-[hsl(var(--h-tone-violet-tint))] p-2">
      <div className="flex items-center justify-between gap-2">
        <h3 ref={headingRef} id={headId} tabIndex={-1}
          className="px-1 text-xs font-bold text-[hsl(var(--h-card-foreground))] focus:outline-none">
          {`${persona} muốn nhắc${count > 0 ? ` · ${count}` : ''}`}
        </h3>
        <button type="button" aria-label={collapsed ? 'Mở' : 'Thu gọn'} title={collapsed ? 'Mở' : 'Thu gọn'}
          aria-expanded={!collapsed} aria-controls={listId} onClick={toggle}
          className="flex size-11 items-center justify-center rounded-md text-[hsl(var(--h-muted-foreground))] transition-colors hover:bg-[hsl(var(--h-muted))] lg:size-6">
          <ChevronDown className={cn('size-4 transition-transform', collapsed && '-rotate-90')} aria-hidden="true" />
        </button>
      </div>
      {!collapsed && (
        <div id={listId}>
          {runCards.length > 0 && (
            <ul data-run-cards="" className="mt-1.5 flex flex-col">
              {runCards.map((c) => <RunCard key={c.runId} card={c} now={now} handlers={runHandlers} />)}
            </ul>
          )}
          {shownDelegated.length > 0 && (
            <div role="group" aria-labelledby={delegatedHeadId} data-tray-delegated="">
              <h4 id={delegatedHeadId}
                className="m-0 px-1 pb-0.5 pt-2 text-[11px] font-semibold uppercase tracking-[0.04em] text-[hsl(var(--h-muted-foreground))]">
                Việc đã giao
              </h4>
              <ul className="mt-1 flex flex-col">{shownDelegated.map(itemOf)}</ul>
            </div>
          )}
          {(shownRest.length > 0 || offers.length > 0 || orphans.length > 0) && <ul className="mt-1.5 flex flex-col">
            {shownRest.map(itemOf)}
            {offers.filter((o) => o.before === null || !visibleIds.has(o.before)).map(offerLine)}
            {orphans.length > 0 && <li data-nudge-orphans="" className="-ml-7">{cards(orphans)}</li>}
          </ul>}
          {items.length > TRAY_ROWS && (
            <div className="pt-1.5 text-center">
              <button type="button" data-tray-more="" onClick={() => setExpanded((e) => !e)}
                className="min-h-11 px-2 text-xs font-semibold text-[hsl(var(--h-primary-hover))] hover:underline lg:min-h-0">
                {expanded ? 'Thu gọn' : `Xem thêm (${items.length - TRAY_ROWS})`}
              </button>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function ItemWithOffers({ offers, offerLine, children }: {
  offers: Offer[]; offerLine: (o: Offer) => ReactNode; children: ReactNode;
}) {
  return (
    <>
      {offers.map(offerLine)}
      {children}
    </>
  );
}
